// Settings → Workflow steps: the job's steps as the same real table as Pipeline steps (Spencer, 2026-10-22; Iris's
// drawing docs/screens/steps-table-screens.html, "the same drawing is Settings → Workflow steps"). A job step has a seat
// (the pipeline's seats) so a job landing on an AI seat starts that employee's run; its level is worked out from the seat
// and whether it needs the Owner's approval. Every value is a picker in its cell, saved on change with Undo on the row;
// Open shows the detail: the condition, the tab it opens and the depth of its check, the role on the job list, the note,
// Instructions and Learned doing it, who changed what and when, Retire. A step is known by its key, never its name.
// Step changes through an AI employee's login (docs/step-changes-brief.md, 2026-10-24) show here as on Pipeline steps: a
// ghost row for an add, the proposed value under the current one, Retire beside a name, the Suggested column, and who, when
// and Undo after a direct write. The Process cell governs both tables; a job step has no cell of its own.

import { useCallback, useState, type ReactNode } from 'react';
import { changedColumns, stepColumnLabel, stepKeyFrom, stepValueWords, type StepChangeRow } from '@zerogap/engine';
import { insertRow, updateRow, useRows, type Row } from '../lib/api';
import { usePipeline } from '../lib/pipeline';
import { useSession } from '../lib/session';
import { Btn, Edit, Fold, Loading, useFolds } from '../ui/kit';
import { nextSort, useDragRows } from '../ui/move';
import { LevelLegend, SeatsStrip, useStepWords } from './PipelineSteps';
import { ChangesField, Field, InstructionsField, LearnedField, Picker, Ro, StepTable, useStepFromAddress, useUndo, type Col, type PickOption } from './steps/StepTable';
import { DirectNote, PendingCards, Prop, SuggestCell, SuggestChip, useSuggest, Was, type CardLine } from './steps/changes';

const ROLES = ['owner', 'office', 'shop', 'contractor', 'client'];
const DEPTHS: PickOption[] = [
  { key: '', label: 'No check' },
  { key: 'quick', label: 'Quick', sub: 'the drawing beside each wall' },
  { key: 'walk', label: 'Walk', sub: 'every item, wall by wall' },
];

const cap = (w: string) => w.charAt(0).toUpperCase() + w.slice(1);

type Step = Row & { id: string; key: string; name: string; sort: number; ghost?: StepChangeRow };

export function WorkflowSteps() {
  const s = useSession();
  const p = usePipeline();
  const steps = useRows('workflow_step', { order: ['sort'] });
  const folds = useFolds('workflow-steps', true);
  const [showRetired, setShowRetired] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const undo = useUndo();
  const { info, levels } = useStepWords();
  const canEdit = s.canEdit('screen.settings');
  const all = steps.rows as Step[];
  const openStep = useCallback(
    (id: string) => {
      setOpen(id);
      if (all.find((x) => x.id === id)?.retired_at) setShowRetired(true);
    },
    [all],
  );
  useStepFromAddress(steps.loading ? [] : all, openStep);
  const openChange = useCallback(
    (c: StepChangeRow) => {
      if (!folds.isOpen('job')) folds.toggle('job');
      if (c.target_id && all.find((x) => x.id === c.target_id)?.retired_at) setShowRetired(true);
    },
    [folds, all],
  );
  const sug = useSuggest('workflow', p, steps.rows, openChange);
  const live = all.filter((x) => !x.retired_at);
  const rows = all.filter((x) => showRetired || !x.retired_at);
  const drag = useDragRows('workflow_step', live, !canEdit);
  if (steps.loading || p.loading) return <Loading what="Loading the workflow steps" />;
  const retired = all.length - live.length;
  const phone = s.device === 'phone';

  const seatName = (id: unknown) => (id ? p.seats.find((x) => x.id === id)?.name ?? '—' : 'No seat');
  const holderOf = (id: unknown) => {
    const seat = p.seats.find((x) => x.id === id);
    if (!seat) return undefined;
    return seat.key === 'client' ? "the lead's primary contact" : (p.users.find((u) => u.id === seat.holder_user_id)?.name as string | undefined) ?? 'nobody';
  };
  const applies = (st: Row) => (st.only_when_attribute ? `${String(st.only_when_attribute).replace(/_/g, ' ')} = ${String(st.only_when_value ?? '').replace(/_/g, ' ')}` : 'Every job');
  const pairs = [...new Map(all.filter((x) => x.only_when_attribute).map((x) => [`${x.only_when_attribute}=${x.only_when_value ?? ''}`, x])).values()];
  const attrs = [...new Set(all.map((x) => x.only_when_attribute as string | null).filter((a): a is string => !!a))];
  const valuesOf = (attr: string) => [...new Set(all.filter((x) => x.only_when_attribute === attr).map((x) => x.only_when_value as string | null).filter((v): v is string => !!v))];
  const tabs = [...new Set(all.map((x) => x.tab as string | null).filter((t): t is string => !!t))];
  // a tab by its name on the job's strip: the name of the step that opens it
  const tabWords = (t: unknown) => (t ? (all.find((x) => x.tab === t)?.name ?? String(t).replace(/_/g, ' ')) : '—');

  const change = (st: Step, field: string, from: string, to: string, patch: Row, back: Row) =>
    void updateRow('workflow_step', st.id, patch).then(() => undo.record({ rowId: st.id, field, words: `${field} → ${to}`, was: `${field}: ${from} → ${to}`, undo: () => updateRow('workflow_step', st.id, back) }));
  const changed = (st: Step, field: string) => undo.note?.rowId === st.id && undo.note.field === field;
  const seatOptions: PickOption[] = [{ key: '', label: 'No seat' }, ...p.seats.map((x) => ({ key: x.id, label: x.name, sub: holderOf(x.id) }))];

  // ---------- the step changes on the job steps ----------
  const ghosts = sug.ghostsIn(null);
  const changeOn = (st: Step) => (st.ghost ? undefined : sug.on(st.id, 'change'));
  const retireOn = (st: Step) => (st.ghost ? undefined : sug.on(st.id, 'retire'));
  const instrOn = (st: Step) => (st.ghost ? undefined : sug.on(st.id, 'instructions'));
  const direct = (st: Step) => (st.ghost ? undefined : sug.direct.get(st.id));
  const ghostLevelOf = (seatId: unknown, gate: unknown): string | null => {
    const seat = p.seats.find((x) => x.id === seatId);
    if (!seat || seat.key === 'client') return null;
    if (seat.key === 'owner') return 'L3';
    return gate ? 'L1' : 'L2';
  };
  const ghostRow = (c: StepChangeRow): Step => ({
    id: `ghost-${c.id}`,
    key: '',
    name: String(sug.proposed(c, 'name') ?? ''),
    sort: 0,
    seat_id: sug.proposed(c, 'seat_id') ?? null,
    level: ghostLevelOf(sug.proposed(c, 'seat_id'), sug.proposed(c, 'gate')),
    only_when_attribute: sug.proposed(c, 'only_when_attribute') ?? null,
    only_when_value: sug.proposed(c, 'only_when_value') ?? null,
    gate: !!sug.proposed(c, 'gate'),
    lock_point: !!sug.proposed(c, 'lock_point'),
    target_offset_days: sug.proposed(c, 'target_offset_days') ?? null,
    tab: sug.proposed(c, 'tab') ?? null,
    check_depth: sug.proposed(c, 'check_depth') ?? null,
    retired_at: null,
    ghost: c,
  });
  const tableRows: Step[] = [];
  {
    const left = [...ghosts];
    let liveSeen = 0;
    for (const r of rows) {
      if (!r.retired_at) {
        for (let i = left.length - 1; i >= 0; i--) if (Number(left[i]!.proposed.position ?? Infinity) <= liveSeen + 1) tableRows.push(ghostRow(left.splice(i, 1)[0]!));
        liveSeen++;
      }
      tableRows.push(r);
    }
    for (const g of left) tableRows.push(ghostRow(g));
  }
  const num = (st: Step): ReactNode => {
    if (st.ghost) return <span className="snew">new</span>;
    if (st.retired_at) return '—';
    const i = live.indexOf(st);
    const before = ghosts.filter((g) => Number(g.proposed.position ?? Infinity) <= i + 1).length;
    return before ? (
      <>
        {i + 1 + before}
        <small className="swas">was {i + 1}</small>
      </>
    ) : (
      String(i + 1)
    );
  };
  const setEdits = (c: StepChangeRow, patch: Row) => {
    for (const [k, v] of Object.entries(patch)) sug.setEdit(c, k, v);
  };
  /** One picker cell: a ghost's proposed value; a live step's picker with the proposal or the old value under it (as on Pipeline steps). */
  const cell = (st: Step, field: string, cols: string[], opts: { label: string; options: PickOption[]; keyOf: (v: Row) => string; wordsOf: (key: string) => ReactNode; subOf?: (v: Row) => ReactNode; patchOf: (key: string) => Row; muted?: (v: Row) => boolean; onLive: (key: string) => void }) => {
    if (st.ghost) {
      const c = st.ghost;
      const v: Row = Object.fromEntries(cols.map((k) => [k, sug.proposed(c, k)]));
      const editing = sug.editing(c) && sug.canDecide && !sug.stale(c);
      return <Picker ghost label={opts.label} value={opts.wordsOf(opts.keyOf(v))} sub={opts.subOf?.(v)} current={opts.keyOf(v)} options={opts.options} disabled={!editing} onPick={(k) => setEdits(c, opts.patchOf(k))} />;
    }
    const c = changeOn(st);
    const proposes = c && cols.some((k) => sug.proposes(c, k) && JSON.stringify(sug.proposed(c, k) ?? null) !== JSON.stringify(st[k] ?? null));
    const d = direct(st);
    const was = d && d.kind === 'change' && cols.some((k) => k in (d.undo ?? {}));
    let under: ReactNode;
    if (c && proposes) under = <Prop dim={sug.stale(c)}>{opts.wordsOf(opts.keyOf(Object.fromEntries(cols.map((k) => [k, sug.proposes(c, k) ? sug.proposed(c, k) : st[k]]))))}</Prop>;
    else if (was) under = <Was>{opts.wordsOf(opts.keyOf(Object.fromEntries(cols.map((k) => [k, k in (d!.undo ?? {}) ? d!.undo![k] : st[k]]))))}</Was>;
    const editingProposal = !!c && proposes && sug.editing(c) && sug.canDecide;
    return (
      <Picker
        label={opts.label}
        value={opts.wordsOf(opts.keyOf(st))}
        sub={opts.subOf?.(st)}
        current={editingProposal ? opts.keyOf(Object.fromEntries(cols.map((k) => [k, sug.proposed(c!, k)]))) : opts.keyOf(st)}
        options={opts.options}
        disabled={!canEdit}
        muted={opts.muted?.(st)}
        changed={changed(st, field)}
        under={under}
        onPick={(k) => (editingProposal ? setEdits(c!, opts.patchOf(k)) : opts.onLive(k))}
      />
    );
  };
  const yesNo = (st: Step, col: 'gate' | 'lock_point', field: string, sub: string) =>
    cell(st, field, [col], {
      label: field,
      options: [
        { key: 'no', label: 'No' },
        { key: 'yes', label: 'Yes', sub },
      ],
      keyOf: (v) => (v[col] ? 'yes' : 'no'),
      wordsOf: (k) => (k === 'yes' ? 'Yes' : 'No'),
      patchOf: (k) => ({ [col]: k === 'yes' }),
      muted: (v) => !v[col],
      onLive: (k) => change(st, field, st[col] ? 'Yes' : 'No', k === 'yes' ? 'Yes' : 'No', { [col]: k === 'yes' }, { [col]: st[col] }),
    });

  const cols: Col<Step>[] = [
    {
      key: 'seat',
      label: 'Seat',
      info: info.seat,
      width: 150,
      cell: (st) =>
        cell(st, 'Seat', ['seat_id'], {
          label: 'Seat',
          options: seatOptions,
          keyOf: (v) => String(v.seat_id ?? ''),
          wordsOf: (k) => seatName(k || null),
          subOf: (v) => holderOf(v.seat_id),
          patchOf: (k) => ({ seat_id: k || null }),
          muted: (v) => !v.seat_id,
          onLive: (k) => change(st, 'Seat', seatName(st.seat_id), seatName(k || null), { seat_id: k || null }, { seat_id: st.seat_id ?? null }),
        }),
    },
    { key: 'lvl', label: 'Lvl', info: info.level, width: 60, cell: (st) => <span className="slvl" title={st.level ? `${st.level}: ${levels[st.level as string] ?? ''}` : 'No seat, or the client'}>{(st.level as string | null) ?? '—'}</span> },
    {
      key: 'applies',
      label: 'Applies when',
      info: info.job_applies_when,
      width: 170,
      cell: (st) =>
        cell(st, 'Applies when', ['only_when_attribute', 'only_when_value'], {
          label: 'Applies when',
          options: [{ key: '', label: 'Every job' }, ...pairs.map((x) => ({ key: `${x.only_when_attribute}=${x.only_when_value ?? ''}`, label: applies(x) }))],
          keyOf: (v) => (v.only_when_attribute ? `${v.only_when_attribute}=${v.only_when_value ?? ''}` : ''),
          wordsOf: (k) => {
            const [attr, ...v] = k.split('=');
            return k ? applies({ only_when_attribute: attr, only_when_value: v.join('=') }) : 'Every job';
          },
          patchOf: (k) => {
            const [attr, ...v] = k.split('=');
            return { only_when_attribute: k ? attr : null, only_when_value: k ? v.join('=') || null : null };
          },
          muted: (v) => !v.only_when_attribute,
          onLive: (k) => {
            const [attr, ...v] = k.split('=');
            change(st, 'Applies when', applies(st), k ? applies({ ...st, only_when_attribute: attr, only_when_value: v.join('=') }) : 'Every job', { only_when_attribute: k ? attr : null, only_when_value: k ? v.join('=') || null : null }, { only_when_attribute: st.only_when_attribute ?? null, only_when_value: st.only_when_value ?? null });
          },
        }),
    },
    { key: 'gate', label: 'Gate', info: info.job_gate, width: 66, cell: (st) => yesNo(st, 'gate', 'Gate', "needs the Owner's approval") },
    { key: 'lock', label: 'Lock point', info: info.lock_point, width: 104, cell: (st) => yesNo(st, 'lock_point', 'Lock point', 'the rows freeze here') },
    {
      key: 'day',
      label: 'Day',
      info: info.target_day,
      width: 70,
      cell: (st) => {
        if (st.ghost) {
          const c = st.ghost;
          const editing = sug.editing(c) && sug.canDecide && !sug.stale(c);
          return editing ? <input className="sday ghost" value={String(sug.proposed(c, 'target_offset_days') ?? '')} aria-label="Proposed day" onChange={(e) => sug.setEdit(c, 'target_offset_days', e.target.value.trim() === '' ? null : Number(e.target.value))} /> : <span className="spk ro ghost"><span className="v">{String(sug.proposed(c, 'target_offset_days') ?? '—')}</span></span>;
        }
        const c = changeOn(st);
        const d = direct(st);
        return (
          <>
            <Edit
              className="sday"
              value={(st.target_offset_days as number | null) ?? ''}
              disabled={!canEdit}
              parse={(t) => (t.trim() === '' || /^\d+$/.test(t.trim()) ? t.trim() : undefined)}
              onSave={(v) => change(st, 'Day', String(st.target_offset_days ?? '—'), v || '—', { target_offset_days: v === '' ? null : Number(v) }, { target_offset_days: st.target_offset_days ?? null })}
            />
            {c && sug.proposes(c, 'target_offset_days') && <Prop dim={sug.stale(c)}>{String(sug.proposed(c, 'target_offset_days') ?? '—')}</Prop>}
            {d && d.kind === 'change' && 'target_offset_days' in (d.undo ?? {}) && <Was>{String(d.undo!.target_offset_days ?? '—')}</Was>}
          </>
        );
      },
    },
    {
      key: 'opens',
      label: 'Opens',
      info: info.opens,
      width: 140,
      cell: (st) =>
        cell(st, 'Opens', ['tab'], {
          label: 'Opens',
          options: [{ key: '', label: '—', sub: 'no tab of its own' }, ...tabs.map((t) => ({ key: t, label: tabWords(t) }))],
          keyOf: (v) => String(v.tab ?? ''),
          wordsOf: (k) => tabWords(k || null),
          subOf: (v) => (v.check_depth && !tabWords(v.tab).toLowerCase().includes(String(v.check_depth)) ? `${v.check_depth} check` : undefined),
          patchOf: (k) => ({ tab: k || null }),
          muted: (v) => !v.tab,
          onLive: (k) => change(st, 'Opens', tabWords(st.tab), tabWords(k || null), { tab: k || null }, { tab: st.tab ?? null }),
        }),
    },
  ];

  const add = async () => {
    const name = window.prompt('Name the job step');
    if (!name?.trim()) return;
    const row = await insertRow('workflow_step', { key: stepKeyFrom(name, new Set(all.map((x) => x.key))), sort: nextSort(all), name: name.trim(), owner_role: 'office' });
    setOpen(row.id as string);
  };

  const words = (col: string, v: unknown): string | null => {
    const label: Record<string, string> = { name: 'Name', seat_id: 'Seat', only_when_attribute: 'Applies when', only_when_value: 'Applies when, value', gate: 'Gate', lock_point: 'Lock point', target_offset_days: 'Day', tab: 'Opens', check_depth: 'Check', owner_role: 'Role', note: 'Note', retired_at: 'Retired', sort: 'Order', instructions: 'Instructions', learned: 'Learned' };
    if (v === undefined) return label[col] ?? null;
    if (v === null || v === '') return '—';
    if (col === 'seat_id') return seatName(v);
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (col === 'retired_at') return 'yes';
    return String(v);
  };

  const cwords = (c: StepChangeRow, col: string, v: unknown) => (col === 'seat_id' ? seatName(v) : col === 'tab' ? tabWords(v) : stepValueWords('workflow_step', col, v, sug.ctx));
  const cardLines = (c: StepChangeRow): CardLine[] => {
    if (c.kind === 'add') {
      const pos = Number(sug.proposed(c, 'position') ?? live.length + 1);
      return [
        { k: 'Step', v: <b>{String(sug.proposed(c, 'name') ?? '')}</b> },
        { k: 'After', v: pos <= 1 ? 'first' : live[pos - 2]?.name ?? 'last' },
        { k: 'Seat', v: seatName(sug.proposed(c, 'seat_id') ?? null) },
        ...(sug.proposed(c, 'only_when_attribute') ? [{ k: 'Applies when', v: applies({ only_when_attribute: sug.proposed(c, 'only_when_attribute'), only_when_value: sug.proposed(c, 'only_when_value') }) }] : []),
        ...(sug.proposed(c, 'gate') ? [{ k: 'Gate', v: 'Yes' }] : []),
        ...(sug.proposed(c, 'tab') ? [{ k: 'Opens', v: tabWords(sug.proposed(c, 'tab')) }] : []),
      ];
    }
    if (c.kind === 'retire') return [{ k: 'Step', v: <b>{sug.ctx.stepName('workflow_step', c.target_id)}</b> }, { k: 'Retire', v: 'behind Show retired; jobs on it keep it' }];
    if (c.kind === 'instructions') return [{ k: 'Step', v: <b>{sug.ctx.stepName('workflow_step', c.target_id)}</b> }, { k: 'Instructions', v: String(sug.proposed(c, 'instructions') ?? '') }];
    const now = sug.nowOf(c) ?? {};
    return [{ k: 'Step', v: <b>{sug.ctx.stepName('workflow_step', c.target_id)}</b> }, ...changedColumns(c).map((k) => ({ k: stepColumnLabel('workflow_step', k), v: <>{cwords(c, k, sug.proposed(c, k))}<small className="swas">was: {cwords(c, k, now[k])}</small></> }))];
  };
  const directLines = (c: StepChangeRow): CardLine[] => {
    const name = sug.ctx.stepName('workflow_step', c.target_id);
    const v = c.applied ?? c.proposed;
    if (c.kind === 'add') return [{ k: 'Job steps', v: <b>{name}</b> }, { k: 'Added', v: `as step ${String(v.position ?? '?')}` }];
    if (c.kind === 'retire') return [{ k: 'Job steps', v: <b>{name}</b> }, { k: 'Retired', v: 'behind Show retired' }];
    if (c.kind === 'instructions') return [{ k: 'Job steps', v: <b>{name}</b> }, { k: 'Instructions', v: 'rewritten' }];
    return [{ k: 'Job steps', v: <b>{name}</b> }, ...changedColumns(c).map((k) => ({ k: stepColumnLabel('workflow_step', k), v: <>{cwords(c, k, v[k])}<small className="swas">was: {cwords(c, k, c.undo?.[k])}</small></> }))];
  };
  const tail = sug.waiting.length
    ? {
        label: 'Suggested',
        info: info.suggested,
        width: 240,
        cell: (st: Step) => {
          const c = st.ghost ?? changeOn(st) ?? retireOn(st) ?? instrOn(st);
          return c ? <SuggestCell s={sug} c={c} buttons={!phone} stepChanged={null} /> : null;
        },
      }
    : undefined;

  return (
    <div className="stack stabs">
      <div className="sph">
        <div>
          <h3>Workflow steps ({live.length})</h3>
          {/* the subtitle went 2026-10-24 (F8); the level legend stays */}
          <div className="ssub">
            <LevelLegend />
          </div>
        </div>
        <label className="check sret">
          <input type="checkbox" className="read" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} /> Show retired · {retired}
        </label>
      </div>
      <SeatsStrip p={p} canEdit={canEdit} />
      {phone && <PendingCards s={sug} lines={cardLines} directLines={directLines} />}
      <Fold
        open={folds.isOpen('job')}
        onToggle={() => folds.toggle('job')}
        label={<span className="sstage">Job steps</span>}
        right={
          <>
            <small className="srule">{folds.isOpen('job') ? "A job moves through them in order; a step that needs the Owner's approval waits for it" : `${live.length} steps`}</small>
            <SuggestChip n={sug.waiting.length} />
          </>
        }
        className="sstg"
      >
        <StepTable<Step>
          cols={cols}
          rows={tableRows}
          num={num}
          retired={(st) => !!st.retired_at}
          ghost={(st) => !!st.ghost}
          lit={(st) => !!sug.lit && (st.ghost?.id === sug.lit || sug.all.find((c) => c.id === sug.lit)?.target_id === st.id)}
          tail={tail}
          name={(st) => {
            if (st.ghost) {
              const c = st.ghost;
              const editing = sug.editing(c) && sug.canDecide && !sug.stale(c);
              return (
                <>
                  {editing ? <input className="sname ghost" value={String(sug.proposed(c, 'name') ?? '')} aria-label="The proposed step's name" onChange={(e) => sug.setEdit(c, 'name', e.target.value)} /> : <span className={`sname ghost${sug.stale(c) ? ' sstale' : ''}`}>{String(sug.proposed(c, 'name') ?? '')}</span>}
                  <span className="schip">suggested</span>
                </>
              );
            }
            const rc = retireOn(st);
            const cc = changeOn(st);
            const d = direct(st);
            return (
              <>
                <Edit className="sname" value={st.name} disabled={!canEdit} onSave={(v) => (v.trim() && v !== st.name ? change(st, 'Name', `“${st.name}”`, `“${v.trim()}”`, { name: v.trim() }, { name: st.name }) : undefined)} />
                {cc && sug.proposes(cc, 'name') && String(sug.proposed(cc, 'name')) !== st.name && <Prop dim={sug.stale(cc)}>{String(sug.proposed(cc, 'name'))}</Prop>}
                {d && d.kind === 'change' && 'name' in (d.undo ?? {}) && <Was>{String(d.undo!.name ?? '—')}</Was>}
                {rc && <span className={`sretire${sug.stale(rc) ? ' sstale' : ''}`}>Retire</span>}
                {st.retired_at && <small>retired</small>}
              </>
            );
          }}
          open={open}
          setOpen={setOpen}
          undo={undo}
          drag={drag}
          canEdit={canEdit}
          flag={(st) => {
            const d = direct(st);
            return d ? <DirectNote s={sug} c={d} /> : null;
          }}
          detail={(st) => (
            <div className="sdg-wrap">
              <div className="sdg">
                <Field label="Applies when · the job's" info={info.job_applies_when}>
                  <Picker
                    label="The job attribute it reads"
                    value={st.only_when_attribute ? String(st.only_when_attribute).replace(/_/g, ' ') : 'Every job'}
                    current={(st.only_when_attribute as string | null) ?? ''}
                    options={[{ key: '', label: 'Every job' }, ...attrs.map((a) => ({ key: a, label: a.replace(/_/g, ' ') })), { key: '+', label: 'Another…', sub: 'name it' }]}
                    disabled={!canEdit}
                    onPick={(k) => {
                      const attr = k === '+' ? window.prompt('The job attribute this step reads (design_lane, cad_check)')?.trim() : k;
                      if (attr === undefined) return;
                      void updateRow('workflow_step', st.id, { only_when_attribute: attr || null, only_when_value: attr && attr === st.only_when_attribute ? st.only_when_value ?? null : null });
                    }}
                  />
                </Field>
                <Field label="Its value">
                  {st.only_when_attribute ? (
                    <Picker
                      label="The value it must have"
                      value={st.only_when_value ? String(st.only_when_value).replace(/_/g, ' ') : '—'}
                      current={(st.only_when_value as string | null) ?? ''}
                      options={[...valuesOf(String(st.only_when_attribute)).map((v) => ({ key: v, label: v.replace(/_/g, ' ') })), { key: '+', label: 'Another…', sub: 'name it' }]}
                      disabled={!canEdit}
                      onPick={(k) => {
                        const v = k === '+' ? window.prompt('The value the job attribute must have')?.trim() : k;
                        if (v) void updateRow('workflow_step', st.id, { only_when_value: v });
                      }}
                    />
                  ) : (
                    <Ro>every job has it</Ro>
                  )}
                </Field>
                {/* Opens is edited in the row's cell only (F11, 2026-10-24); the Level is read, worked out from the seat and the gate */}
                <Field label="Level" info={info.level}>
                  <Ro>
                    {st.level ? `${st.level} · ${levels[st.level as string] ?? ''}` : 'no seat, or the client'}
                    <small className="smeta" style={{ display: 'block' }}>set by the seat and the done-when</small>
                  </Ro>
                </Field>
                <Field label="Its check">
                  <Picker label="Its check" value={DEPTHS.find((d) => d.key === (st.check_depth ?? ''))?.label ?? String(st.check_depth)} current={(st.check_depth as string | null) ?? ''} options={DEPTHS} disabled={!canEdit} onPick={(k) => void updateRow('workflow_step', st.id, { check_depth: k || null })} />
                </Field>
                <Field label="Note" wide>
                  <Edit multiline className="sin ta" value={(st.note as string | null) ?? ''} disabled={!canEdit} onSave={(v) => updateRow('workflow_step', st.id, { note: v || null })} />
                </Field>
                <Field label="Role on the job list" info="Whose column the job sits in on the Jobs list while it is on this step.">
                  <Picker label="Role" value={st.owner_role ? cap(String(st.owner_role)) : '—'} current={(st.owner_role as string | null) ?? ''} options={ROLES.map((r) => ({ key: r, label: cap(r) }))} disabled={!canEdit} onPick={(k) => void updateRow('workflow_step', st.id, { owner_role: k })} />
                </Field>
                <ChangesField table="workflow_step" step={st} users={p.users} words={words} />
                <InstructionsField table="workflow_step" step={st} info={info.instructions} users={p.users} />
                <LearnedField table="workflow_step" step={st} info={info.learned} users={p.users} />
              </div>
              <div className="sdfoot">
                <span>Changes save as you make them</span>
                <span className="sp" />
                {canEdit &&
                  (st.retired_at ? (
                    <button type="button" className="sob" onClick={() => void updateRow('workflow_step', st.id, { retired_at: null })}>
                      Restore
                    </button>
                  ) : (
                    <button type="button" className="sob dim" onClick={() => void updateRow('workflow_step', st.id, { retired_at: new Date().toISOString() }).then(() => setOpen(null))}>
                      Retire this step
                    </button>
                  ))}
                <button type="button" className="sob" onClick={() => setOpen(null)}>
                  Close
                </button>
              </div>
            </div>
          )}
          foot={
            canEdit ? (
              <div className="saddrow">
                <Btn small onClick={() => void add()}>
                  + Add a step
                </Btn>
                <small>lands here, at the end</small>
              </div>
            ) : null
          }
        />
      </Fold>
    </div>
  );
}
